import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  AbortSourceCodeUploadBodySchema,
  CompleteSourceCodeUploadBodySchema,
  CompleteSourceCodeUploadResultSchema,
  InitiateSourceCodeUploadBodySchema,
  InitiateSourceCodeUploadResultSchema,
  SignSourceCodeUploadPartsBodySchema,
  SignSourceCodeUploadPartsResultSchema,
  ZipRepositoryFileNameSchema,
} from '@terra/shared-schemas-git';
import { ApplicationsIdSchema } from './git.schema';

const MessageErrorDataSchema = z.object({ message: z.string() });

const sourceCodeUploadErrors = {
  FORBIDDEN: { status: 403, data: MessageErrorDataSchema },
  INTERNAL_SERVER_ERROR: { status: 500, data: MessageErrorDataSchema },
} as const;

export const gitSourceCodeUploadContract = {
  getSourceCodeUploadUrl: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/source-code/upload-url',
      summary: 'Get presigned URL for uploading source code ZIP',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: z.object({
          fileName: ZipRepositoryFileNameSchema,
        }),
      }),
    )
    .output(z.object({ uploadUrl: z.string(), key: z.string() }))
    .errors(sourceCodeUploadErrors),
  initiateSourceCodeUpload: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/source-code/uploads/initiate',
      summary:
        'Start a source code ZIP upload; returns a single PUT URL or a multipart upload plan',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: InitiateSourceCodeUploadBodySchema,
      }),
    )
    .output(InitiateSourceCodeUploadResultSchema)
    .errors(sourceCodeUploadErrors),
  signSourceCodeUploadParts: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/source-code/uploads/parts',
      summary: 'Get short-lived presigned URLs for multipart upload parts',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: SignSourceCodeUploadPartsBodySchema,
      }),
    )
    .output(SignSourceCodeUploadPartsResultSchema)
    .errors(sourceCodeUploadErrors),
  completeSourceCodeUpload: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/source-code/uploads/complete',
      summary: 'Complete a multipart source code ZIP upload',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: CompleteSourceCodeUploadBodySchema,
      }),
    )
    .output(CompleteSourceCodeUploadResultSchema)
    .errors({
      ...sourceCodeUploadErrors,
      BAD_REQUEST: { status: 400, data: MessageErrorDataSchema },
    }),
  abortSourceCodeUpload: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/source-code/uploads/abort',
      summary: 'Abort a multipart source code ZIP upload',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: AbortSourceCodeUploadBodySchema,
      }),
    )
    .output(z.object({ success: z.boolean() }))
    .errors(sourceCodeUploadErrors),
} as const;
